import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import AdminLayout from "../../../../layouts/admin/AdminLayout";
import "../../../../assets/css/admin/dashboardcss/AdminDeals.css";
import AppContext from "../../../../context/AppContext";
import axios from "axios";
import { checkLoggedInAdmin } from "../../../../helper/CustomHelper";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

export default function AdminDeals() {
  const { baseUrl } = useContext(AppContext);
  const navigate = useNavigate();

  const [Title, setTitle] = useState("");
  const [Description, setDescription] = useState("");
  const [Discount, setDiscount] = useState("");
  const [Tag, setTag] = useState("");

  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [editDeal, setEditDeal] = useState(null);
  const [errors, setErrors] = useState({});

  // =========================
  // View All Deals
  // =========================
  const viewAllDeals = () => {
    axios
      .get(`${baseUrl}/admin/deals/viewAllDeals`)
      .then((res) => {
        setDeals(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // =========================
  // Page Load
  // =========================
  useEffect(() => {
    checkLoggedInAdmin(navigate);
    viewAllDeals();
  }, []);

  // =========================
  // Form Validation
  // =========================
  const isFormValid = () => {
    return (
      Title.trim() !== "" &&
      Description.trim() !== "" &&
      Discount.trim() !== "" &&
      Tag.trim() !== ""
    );
  };

  // =========================
  // Reset Form
  // =========================
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setDiscount("");
    setTag("");
    setErrors({});
    setEditDeal(null);
  };

  // =========================
  // Add Deal
  // =========================
  const handleSaveAdd = () => {
    if (!isFormValid()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all fields before saving.",
        confirmButtonColor: "#E8879D",
      });

      return;
    }

    setIsLoading(true);

    const formData = new FormData();

    formData.append("title", Title);
    formData.append("description", Description);
    formData.append("discount", Discount);
    formData.append("tag", Tag);

    axios
      .post(`${baseUrl}/admin/deals/addNewDeal`, formData)
      .then(() => {
        viewAllDeals();

        setShowAddModal(false);
        resetForm();

        Swal.fire({
          icon: "success",
          title: "Deal Added!",
          text: "New deal has been created successfully.",
          confirmButtonColor: "#E8879D",
        });
      })
      .catch((err) => {
        console.log(err);

        if (err.response?.status === 422) {
          setErrors(err.response.data.errors || {});
        }

        Swal.fire({
          icon: "error",
          title: "Save Failed!",
          text:
            err.response?.data?.message ||
            "Unable to create deal. Please try again.",
          confirmButtonColor: "#E8879D",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // =========================
  // Edit Deal
  // =========================
  const handleSaveEdit = () => {
    if (!isFormValid()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all fields before saving.",
        confirmButtonColor: "#E8879D",
      });

      return;
    }

    setIsLoading(true);

    const formData = new FormData();

    formData.append("id", editDeal.id);
    formData.append("title", Title);
    formData.append("description", Description);
    formData.append("discount", Discount);
    formData.append("tag", Tag);

    axios
      .post(`${baseUrl}/admin/deals/editDeal`, formData)
      .then(() => {
        viewAllDeals();

        setShowEditModal(false);
        resetForm();

        Swal.fire({
          icon: "success",
          title: "Deal Updated!",
          text: "Deal has been updated successfully.",
          confirmButtonColor: "#E8879D",
        });
      })
      .catch((err) => {
        console.log(err);

        if (err.response?.status === 422) {
          setErrors(err.response.data.errors || {});
        }

        Swal.fire({
          icon: "error",
          title: "Update Failed!",
          text:
            err.response?.data?.message ||
            "Unable to update deal. Please try again.",
          confirmButtonColor: "#E8879D",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // =========================
  // Delete Deal
  // =========================
  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete this deal?",
      text: "This deal will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF7C7C",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      axios
        .delete(`${baseUrl}/admin/deals/deleteDeal/${id}`)
        .then(() => {
          viewAllDeals();

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Deal has been removed successfully.",
            confirmButtonColor: "#E8879D",
          });
        })
        .catch((err) => {
          console.log(err);

          Swal.fire({
            icon: "error",
            title: "Delete Failed!",
            text:
              err.response?.data?.message ||
              "Unable to delete deal. Please try again.",
            confirmButtonColor: "#E8879D",
          });
        });
    });
  };

  // =========================
  // Open Edit Modal
  // =========================
  const handleOpenEdit = (deal) => {
    setEditDeal(deal);

    setTitle(deal.title || "");
    setDescription(deal.description || "");
    setDiscount(deal.discount || "");
    setTag(deal.tag || "");

    setErrors({});
    setShowEditModal(true);
  };

  // =========================
  // Open Add Modal
  // =========================
  const handleOpenAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  // =========================
  // Close Add Modal
  // =========================
  const closeAddModal = () => {
    if (isLoading) {
      return;
    }

    setShowAddModal(false);
    resetForm();
  };

  // =========================
  // Close Edit Modal
  // =========================
  const closeEditModal = () => {
    if (isLoading) {
      return;
    }

    setShowEditModal(false);
    resetForm();
  };

  return (
    <AdminLayout>
      <div className="esl-deals-wrapper">

        {/* ================= HEADER ================= */}
        <div className="esl-deals-header">
          <div>
            <h1 className="esl-deals-title">
              Deals
            </h1>

            <p className="esl-deals-subtitle">
              Manage promotional offers.
            </p>
          </div>

          <button
            className="esl-add-deal-btn"
            onClick={handleOpenAdd}
            disabled={isLoading}
          >
            <span className="esl-add-icon">
              +
            </span>

            Add deal
          </button>
        </div>

        {/* ================= DEAL CARDS ================= */}
        <Container fluid className="px-0">
          <Row className="g-4">

            {deals.map((deal, index) => (
              <Col
                key={deal.id}
                xs={12}
                sm={6}
                xl={4}
              >
                <div
                  className="esl-deal-card"
                  style={{
                    animationDelay: `${index * 0.08}s`,
                  }}
                >

                  <div className="esl-deal-card-top">

                    <span className="esl-deal-discount">
                      {deal.discount}
                    </span>

                    {deal.tag && (
                      <span className="esl-deal-tag">
                        {deal.tag}
                      </span>
                    )}

                  </div>

                  <h3 className="esl-deal-name">
                    {deal.title}
                  </h3>

                  <p className="esl-deal-desc">
                    {deal.description}
                  </p>

                  <div className="esl-deal-actions">

                    {/* EDIT */}
                    <button
                      className="esl-btn-edit"
                      onClick={() => handleOpenEdit(deal)}
                      disabled={isLoading}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />

                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>

                      Edit
                    </button>

                    {/* DELETE */}
                    <button
                      className="esl-btn-delete"
                      onClick={() => handleDelete(deal.id)}
                      disabled={isLoading}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />

                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />

                        <path d="M10 11v6M14 11v6" />

                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>

                  </div>
                </div>
              </Col>
            ))}

          </Row>
        </Container>

        {/* ================= ADD MODAL ================= */}
        {showAddModal && (
          <>
            <div
              className="modal show d-block esl-modal"
              tabIndex="-1"
            >
              <div className="modal-dialog modal-dialog-centered esl-modal-dialog">

                <div className="modal-content esl-modal-content">

                  <div className="esl-modal-header">

                    <h5 className="esl-modal-title">
                      New deal
                    </h5>

                    <button
                      className="esl-modal-close"
                      onClick={closeAddModal}
                      disabled={isLoading}
                    >
                      ×
                    </button>

                  </div>

                  <div className="esl-modal-body">

                    {/* TITLE */}
                    <div className="esl-form-group">

                      <label className="esl-form-label">
                        Title
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={Title}
                        onChange={(e) =>
                          setTitle(e.target.value)
                        }
                        disabled={isLoading}
                      />

                      {errors.title && (
                        <p className="text-danger small mt-1">
                          {errors.title}
                        </p>
                      )}

                    </div>

                    {/* DESCRIPTION */}
                    <div className="esl-form-group">

                      <label className="esl-form-label">
                        Description
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={Description}
                        onChange={(e) =>
                          setDescription(e.target.value)
                        }
                        disabled={isLoading}
                      />

                      {errors.description && (
                        <p className="text-danger small mt-1">
                          {errors.description}
                        </p>
                      )}

                    </div>

                    <Row className="g-3">

                      {/* DISCOUNT */}
                      <Col xs={12} md={6}>

                        <div className="esl-form-group">

                          <label className="esl-form-label">
                            Discount
                          </label>

                          <input
                            type="text"
                            className="esl-form-input"
                            placeholder="20% OFF"
                            value={Discount}
                            onChange={(e) =>
                              setDiscount(e.target.value)
                            }
                            disabled={isLoading}
                          />

                          {errors.discount && (
                            <p className="text-danger small mt-1">
                              {errors.discount}
                            </p>
                          )}

                        </div>

                      </Col>

                      {/* TAG */}
                      <Col xs={12} md={6}>

                        <div className="esl-form-group">

                          <label className="esl-form-label">
                            Tag
                          </label>

                          <select
                            className="esl-form-input"
                            value={Tag}
                            onChange={(e) =>
                              setTag(e.target.value)
                            }
                            disabled={isLoading}
                          >
                            <option value="">
                              -- Select Tag --
                            </option>

                            <option value="Bestseller">
                              Bestseller
                            </option>

                            <option value="Bundle">
                              Bundle
                            </option>

                            <option value="Limited">
                              Limited
                            </option>

                            <option value="New">
                              New
                            </option>

                            <option value="Flash Sale">
                              Flash Sale
                            </option>

                            <option value="Loyalty">
                              Loyalty
                            </option>

                          </select>

                          {errors.tag && (
                            <p className="text-danger small mt-1">
                              {errors.tag}
                            </p>
                          )}

                        </div>

                      </Col>

                    </Row>

                    {/* SAVE */}
                    <div className="esl-modal-footer-actions">

                      <button
                        className="esl-btn-save"
                        onClick={handleSaveAdd}
                        disabled={isLoading}
                      >
                        {isLoading
                          ? "Saving..."
                          : "Save"}
                      </button>

                    </div>

                  </div>
                </div>
              </div>
            </div>

            <div
              className="modal-backdrop fade show"
              onClick={closeAddModal}
            />
          </>
        )}

        {/* ================= EDIT MODAL ================= */}
        {showEditModal && editDeal && (
          <>
            <div
              className="modal show d-block esl-modal"
              tabIndex="-1"
            >
              <div className="modal-dialog modal-dialog-centered esl-modal-dialog">

                <div className="modal-content esl-modal-content">

                  <div className="esl-modal-header">

                    <h5 className="esl-modal-title">
                      Edit deal
                    </h5>

                    <button
                      className="esl-modal-close"
                      onClick={closeEditModal}
                      disabled={isLoading}
                    >
                      ×
                    </button>

                  </div>

                  <div className="esl-modal-body">

                    {/* TITLE */}
                    <div className="esl-form-group">

                      <label className="esl-form-label">
                        Title
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={Title}
                        onChange={(e) =>
                          setTitle(e.target.value)
                        }
                        disabled={isLoading}
                      />

                      {errors.title && (
                        <p className="text-danger small mt-1">
                          {errors.title}
                        </p>
                      )}

                    </div>

                    {/* DESCRIPTION */}
                    <div className="esl-form-group">

                      <label className="esl-form-label">
                        Description
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={Description}
                        onChange={(e) =>
                          setDescription(e.target.value)
                        }
                        disabled={isLoading}
                      />

                      {errors.description && (
                        <p className="text-danger small mt-1">
                          {errors.description}
                        </p>
                      )}

                    </div>

                    <Row className="g-3">

                      {/* DISCOUNT */}
                      <Col xs={12} md={6}>

                        <div className="esl-form-group">

                          <label className="esl-form-label">
                            Discount
                          </label>

                          <input
                            type="text"
                            className="esl-form-input"
                            value={Discount}
                            onChange={(e) =>
                              setDiscount(e.target.value)
                            }
                            disabled={isLoading}
                          />

                          {errors.discount && (
                            <p className="text-danger small mt-1">
                              {errors.discount}
                            </p>
                          )}

                        </div>

                      </Col>

                      {/* TAG */}
                      <Col xs={12} md={6}>

                        <div className="esl-form-group">

                          <label className="esl-form-label">
                            Tag
                          </label>

                          <select
                            className="esl-form-input"
                            value={Tag}
                            onChange={(e) =>
                              setTag(e.target.value)
                            }
                            disabled={isLoading}
                          >
                            <option value="">
                              -- Select Tag --
                            </option>

                            <option value="Bestseller">
                              Bestseller
                            </option>

                            <option value="Bundle">
                              Bundle
                            </option>

                            <option value="Limited">
                              Limited
                            </option>

                            <option value="New">
                              New
                            </option>

                            <option value="Flash Sale">
                              Flash Sale
                            </option>

                            <option value="Loyalty">
                              Loyalty
                            </option>

                          </select>

                          {errors.tag && (
                            <p className="text-danger small mt-1">
                              {errors.tag}
                            </p>
                          )}

                        </div>

                      </Col>

                    </Row>

                    {/* UPDATE */}
                    <div className="esl-modal-footer-actions">

                      <button
                        className="esl-btn-save"
                        onClick={handleSaveEdit}
                        disabled={isLoading}
                      >
                        {isLoading
                          ? "Updating..."
                          : "Update"}
                      </button>

                    </div>

                  </div>
                </div>
              </div>
            </div>

            <div
              className="modal-backdrop fade show"
              onClick={closeEditModal}
            />
          </>
        )}

      </div>
    </AdminLayout>
  );
}